/**
 * FileUploadDropzone Component
 *
 * Drag-and-drop file upload interface for Houston Marketing Calendar Excel files.
 * Supports:
 * - Drag and drop .xlsx files
 * - Click to browse file selection
 * - File validation (type and size)
 * - Upload progress indicator
 * - Mobile-responsive design (44px touch targets)
 */

import React, { useCallback, useState, useRef } from 'react';

interface FileUploadDropzoneProps {
  onFileSelect: (file: File) => void;
  isUploading: boolean;
  uploadProgress?: number;
  error?: string | null;
  accept?: string;
  maxSizeMB?: number;
}

// Icons
const UploadIcon = () => (
  <svg className="w-12 h-12 text-dark-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
    />
  </svg>
);

const FileIcon = () => (
  <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
  onFileSelect,
  isUploading,
  uploadProgress = 0,
  error,
  accept = '.xlsx',
  maxSizeMB = 10,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file extension
      const extension = file.name.toLowerCase().split('.').pop();
      if (extension !== 'xlsx') {
        return 'Only .xlsx files are allowed. Please select an Excel file.';
      }

      // Check file size
      const maxBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxBytes) {
        return `File is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum size is ${maxSizeMB}MB.`;
      }

      return null;
    },
    [maxSizeMB]
  );

  const handleFile = useCallback(
    (file: File) => {
      const error = validateFile(file);
      if (error) {
        setValidationError(error);
        setSelectedFile(null);
        return;
      }

      setValidationError(null);
      setSelectedFile(file);
    },
    [validateFile]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedFile(null);
    setValidationError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleUpload = useCallback(() => {
    if (selectedFile) {
      onFileSelect(selectedFile);
    }
  }, [selectedFile, onFileSelect]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const displayError = validationError || error;

  return (
    <div className="w-full" data-testid="hmc-upload-dropzone">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
        data-testid="hmc-upload-dropzone-input"
        disabled={isUploading}
      />

      {/* Dropzone area */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={!selectedFile && !isUploading ? handleBrowseClick : undefined}
        className={`
          relative border-2 border-dashed rounded-xl p-6 md:p-8 text-center transition-all duration-200
          ${
            isDragActive
              ? 'border-brand-500 bg-brand-500/10'
              : selectedFile
                ? 'border-green-500/50 bg-green-500/5'
                : 'border-dark-border/30 hover:border-gray-500 cursor-pointer'
          }
          ${isUploading ? 'pointer-events-none opacity-75' : ''}
        `}
      >
        {/* Upload progress overlay */}
        {isUploading && (
          <div className="absolute inset-0 bg-dark-surface/80 rounded-xl flex flex-col items-center justify-center z-10">
            <div className="w-16 h-16 mb-4">
              <svg className="animate-spin w-full h-full text-brand-500" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <p className="text-white font-medium">Uploading...</p>
            {uploadProgress > 0 && (
              <div className="w-48 h-2 bg-dark-border rounded-full mt-3 overflow-hidden">
                <div
                  className="h-full bg-brand-500 rounded-full transition-all duration-200"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* No file selected - show dropzone */}
        {!selectedFile && (
          <>
            <UploadIcon />
            <p className="mt-4 text-white font-medium">
              {isDragActive ? 'Drop your file here' : 'Drag & drop your Excel file here'}
            </p>
            <p className="mt-2 text-sm text-dark-muted">
              or{' '}
              <button
                type="button"
                onClick={handleBrowseClick}
                className="text-brand-400 hover:text-brand-300 underline font-medium min-h-[44px] inline-flex items-center"
                disabled={isUploading}
              >
                browse files
              </button>
            </p>
            <p className="mt-4 text-xs text-dark-muted">
              Accepts .xlsx files up to {maxSizeMB}MB
            </p>
          </>
        )}

        {/* File selected - show file info */}
        {selectedFile && (
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-4 p-4 bg-dark-border/50 rounded-lg w-full max-w-sm">
              <FileIcon />
              <div className="flex-1 min-w-0 text-left">
                <p className="text-white font-medium truncate">{selectedFile.name}</p>
                <p className="text-sm text-dark-muted">{formatFileSize(selectedFile.size)}</p>
              </div>
              <button
                type="button"
                onClick={handleClearSelection}
                className="p-2 text-dark-muted hover:text-white hover:bg-dark-border rounded-lg transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Remove file"
                disabled={isUploading}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Upload button */}
            <button
              type="button"
              onClick={handleUpload}
              disabled={isUploading}
              className="mt-4 px-6 py-3 bg-brand-500 text-white font-medium rounded-lg hover:bg-brand-600 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              data-testid="hmc-upload-button"
            >
              Upload and Process
            </button>
          </div>
        )}
      </div>

      {/* Error message */}
      {displayError && (
        <div
          className="mt-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-sm"
          data-testid="hmc-upload-error"
        >
          {displayError}
        </div>
      )}
    </div>
  );
};

export default FileUploadDropzone;
